/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ModalController, NavController } from '@ionic/angular';
import { SelectCountryModalComponent } from 'src/app/modules/shared/modals/select-country-modal/select-country-modal.component';
import { AccountService } from 'src/app/services/user/account.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { UiService } from 'src/app/services/ui.service';
import { UserGuestListV2Component } from '../user-guest-list-v2/user-guest-list-v2.component';
import { ThemeService } from 'src/app/services/client/theme/theme.service';

@Component({
  selector: 'app-passenger-detail-form-v2',
  templateUrl: './passenger-detail-form-v2.component.html',
  styleUrls: ['./passenger-detail-form-v2.component.scss'],
})
export class PassengerDetailFormV2Component implements OnInit {
  @Input() flightInfo: any;
  @Input() passengerList: any;
  @Input() userInfo: any;
  @Input() type: string;
  @Input() editedPassengerDetail: any;
  @Input() indexofEditedPassenger: any;
  @Input() JourneyType: any;

  public dobValidation: any;
  public formFields: any;
  public passengerForm: UntypedFormGroup;
  public dupPax: boolean;
  public firstFlightDate: any;
  public lastFlightDate: any;
  public userGuestList: any;
  public dobError: string = '';
  public titleGenderError: string = '';

  public currentHeaderData: any = {
    modalSlotEnd: true,
    marl20: true,
    title: 'flight.passenger_detail_form.header',
  };

  public selectedNationalityData: any = null;
  public partnerPid: string = '';

  public passengerDetail: any = {
    Title: '',
    FirstName: '',
    LastName: '',
    Gender: '',
    DateOfBirth: '',
    Nationality: null,
  };
  public isGuestListModalOpen: boolean = false;
  public saveGuest: boolean = false;

  private guestListCache: any[] | null = null;

  constructor(
    private navController: NavController,
    private modalController: ModalController,
    private formBuilder: UntypedFormBuilder,
    private accountService: AccountService,
    private analyticsService: AnalyticsService,
    private uiService: UiService,
    private themeService: ThemeService
  ) {}

  private namesNotSameValidator(group: UntypedFormGroup) {
    const firstName = group.get('FirstName')?.value;
    const lastName = group.get('LastName')?.value;

    if (
      firstName &&
      lastName &&
      firstName.trim().toLowerCase() === lastName.trim().toLowerCase()
    ) {
      return { namesAreSame: true };
    }
    return null;
  }

  private getExpectedGenderForTitle(title: string): string | null {
    switch ((title || '').trim()) {
      case 'Mr':
      case 'Mstr':
        return 'Male';
      case 'Mrs':
      case 'Ms':
      case 'Miss':
        return 'Female';
      case 'Dr':
        return null;
      default:
        return null;
    }
  }

  private validateTitleGenderCompatibility(): void {
    if (!this.passengerForm) {
      return;
    }

    const title = this.passengerForm.get('Title')?.value;
    const gender = this.passengerForm.get('Gender')?.value;
    const titleControl = this.passengerForm.get('Title');
    const expectedGender = this.getExpectedGenderForTitle(title);

    if (!titleControl) {
      return;
    }

    const currentErrors = { ...(titleControl.errors || {}) };
    delete currentErrors.invalidTitleGender;

    if (expectedGender && gender && gender !== expectedGender) {
      titleControl.setErrors({
        ...currentErrors,
        invalidTitleGender: true,
      });
      titleControl.markAsTouched();
      this.titleGenderError = `Title ${title} supports ${expectedGender.toLowerCase()} gender only`;
      return;
    }

    titleControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    this.titleGenderError = '';
  }

  private syncGenderWithTitle(title: string): void {
    const expectedGender = this.getExpectedGenderForTitle(title);
    if (!expectedGender || !this.passengerForm) {
      this.validateTitleGenderCompatibility();
      return;
    }

    this.passengerForm.get('Gender')?.setValue(expectedGender);
    this.validateTitleGenderCompatibility();
  }

  private validateDateOfBirth = (control: UntypedFormControl) => {
    const date = control.value;
    if (!date) {
      this.dobError = 'flight.passenger_detail_form.date_of_birth_is_required';
      return { required: true };
    }

    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      this.dobError =
        'flight.passenger_detail_form.date_of_birth_cannot_be_in_future';
      return { futureDate: true };
    }

    const minDate = this.dobValidation?.min
      ? new Date(this.dobValidation.min)
      : null;
    const maxDate = this.dobValidation?.max
      ? new Date(this.dobValidation.max)
      : null;
    if (minDate) minDate.setHours(0, 0, 0, 0);
    if (maxDate) maxDate.setHours(0, 0, 0, 0);

    switch (this.type) {
      case 'adult':
        if (minDate && selectedDate < minDate) {
          this.dobError =
            'flight.passenger_detail_form.passenger_cannot_be_more_than_100_years_old_Please_select_a_valid_date';
          return { tooOld: true };
        }
        if (maxDate && selectedDate > maxDate) {
          this.dobError =
            'flight.passenger_detail_form.passenger_must_be_more_than_12_years_old_Please_select_a_valid_date';
          return { tooYoung: true };
        }
        break;

      case 'child':
        if (this.isReturnFlight() && this.lastFlightDate) {
          const returnDate = new Date(this.lastFlightDate);
          const ageOnReturn = this.calculateAgeInYears(
            selectedDate,
            returnDate
          );
          if (ageOnReturn >= 12) {
            this.dobError =
              'flight.passenger_detail_form.the_return_flight_cannot_be_booked_as_the_passenger_must_be_under_12_years_old';
            return { returnFlightAge: true };
          }
        }
        if (minDate && selectedDate < minDate) {
          this.dobError =
            'flight.passenger_detail_form.passenger_must_be_at_least_2_years_old_to_be_considered_a_child';
          return { invalidChildAge: true };
        }
        if (maxDate && selectedDate > maxDate) {
          this.dobError =
            'flight.passenger_detail_form.passenger_must_be_under_12_years_old_to_be_considered_a_child';
          return { tooYoungForChild: true };
        }
        break;

      case 'infant':
        if (this.isReturnFlight() && this.lastFlightDate) {
          const returnDate = new Date(this.lastFlightDate);
          const ageOnReturn = this.calculateAgeInYears(
            selectedDate,
            returnDate
          );
          if (ageOnReturn >= 2) {
            this.dobError =
              'flight.passenger_detail_form.the_return_flight_cannot_be_booked_as_the_passenger_must_be_under_2_years_old';
            return { returnFlightAge: true };
          }
        }
        if (minDate && selectedDate < minDate) {
          this.dobError =
            'flight.passenger_detail_form.passenger_must_be_under_2_years_old_to_be_considered_an_infant';
          return { invalidInfantAge: true };
        }
        if (maxDate && selectedDate > maxDate) {
          this.dobError =
            'flight.passenger_detail_form.passenger_must_be_under_2_years_old_to_be_considered_an_infant';
          return { tooYoungForInfant: true };
        }
        break;

      default:
        this.dobError = '';
        return { invalidType: true };
    }

    this.dobError = '';
    return null;
  };

  private calculateAgeInYears(birthDate: Date, referenceDate: Date): number {
    const birth = new Date(birthDate);
    const reference = new Date(referenceDate);

    let age = reference.getFullYear() - birth.getFullYear();
    const monthDiff = reference.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && reference.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  }

  private isReturnFlight(): boolean {
    if (this.JourneyType === 2 || this.JourneyType === 'Return') {
      return true;
    }

    const allFlights = this.flightInfo?.Segments;
    return Array.isArray(allFlights) && allFlights.length > 1;
  }

  ngOnInit() {
    this.partnerPid = this.themeService.getAppPid;
    const allFlights = Array.isArray(this.flightInfo?.Segments)
      ? this.flightInfo.Segments.flat()
      : [];
    this.firstFlightDate = allFlights.length
      ? new Date(allFlights[0].DepartureTime)
      : new Date();

    this.lastFlightDate =
      allFlights?.length > 1
        ? new Date(allFlights[allFlights.length - 1].DepartureTime)
        : this.firstFlightDate;

    this.dobValidation = this.getAgeLimits();

    if (this.editedPassengerDetail) {
      this.passengerDetail = {
        Title: this.editedPassengerDetail.Title,
        FirstName: this.editedPassengerDetail.FirstName,
        LastName: this.editedPassengerDetail.LastName,
        Gender: this.editedPassengerDetail.Gender,
        DateOfBirth: this.editedPassengerDetail.DateOfBirth,
        Nationality: this.editedPassengerDetail.Nationality,
      };
      this.type = this.editedPassengerDetail.type;
      this.dobValidation = this.getAgeLimits();
      // Restore saveGuest state when reopening in edit mode
      this.saveGuest = !!this.editedPassengerDetail.saveGuest;
    } else if ((this.passengerList ?? []).length === 0) {
      this.passengerDetail = {
        ...this.passengerDetail,
        FirstName: this.joinByGapExcludeLast(this.userInfo?.name || ''),
        LastName: this.explodeByGapAndReturnLast(this.userInfo?.name || ''),
        DateOfBirth: this.userInfo?.dob || '',
      };
    }

    if (!this.passengerDetail.Nationality) {
      const defaultNat = this.themeService.getDefaultNationality;
      if (defaultNat) {
        this.passengerDetail.Nationality = {
          CountryCode: defaultNat.iso2,
          CountryName: defaultNat.nationality,
        };
      }
    }

    if (this.passengerDetail.Nationality) {
      this.selectedNationalityData = {
        iso2: this.passengerDetail.Nationality.CountryCode,
        nationality: this.passengerDetail.Nationality.CountryName,
        name: this.passengerDetail.Nationality.CountryName,
      };
    }

    this.formFields = {
      Title: new UntypedFormControl(this.passengerDetail.Title, [
        Validators.required,
      ]),
      FirstName: new UntypedFormControl(this.passengerDetail.FirstName, [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern('^[a-zA-Z ]*$'),
      ]),
      LastName: new UntypedFormControl(this.passengerDetail.LastName, [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern('^[a-zA-Z ]*$'),
      ]),
      Gender: new UntypedFormControl(this.passengerDetail.Gender, [
        Validators.required,
      ]),
      DateOfBirth: new UntypedFormControl(this.passengerDetail.DateOfBirth, [
        Validators.required,
        this.validateDateOfBirth,
      ]),
      Nationality: new UntypedFormControl(
        this.passengerDetail?.Nationality?.CountryName,
        [Validators.required]
      ),
    };

    this.passengerForm = new UntypedFormGroup(this.formFields);
    this.passengerForm.get('Title')?.valueChanges.subscribe((title: string) => {
      this.syncGenderWithTitle(title);
    });
    this.passengerForm.get('Gender')?.valueChanges.subscribe(() => {
      this.validateTitleGenderCompatibility();
    });
    this.syncGenderWithTitle(this.passengerForm.get('Title')?.value);
    this.dupPax = false;
  }

  getTitleErrorMessage(): string {
    const control = this.passengerForm.get('Title');
    if (control?.touched && control?.errors?.invalidTitleGender) {
      return this.titleGenderError;
    }
    return '';
  }

  getDobErrorMessage(): string {
    const control = this.passengerForm.get('DateOfBirth');
    if (control?.errors && control?.touched) {
      return this.dobError || 'Date of birth is invalid';
    }
    return '';
  }

  onDobBlur() {
    const control = this.passengerForm.get('DateOfBirth');
    control?.markAsTouched({ onlySelf: true });
    control?.updateValueAndValidity({ onlySelf: true });
  }

  explodeByGapAndReturnLast(str: string): string {
    if (!str) return '';
    const parts = str.trim().split(/\s+/);
    return parts[parts.length - 1] || '';
  }

  joinByGapExcludeLast(str: string): string {
    if (!str) return '';
    const parts = str.trim().split(/\s+/);
    if (parts.length <= 1) return '';
    return parts.slice(0, -1).join(' ');
  }

  getAgeLimits() {
    const travelDate = new Date(this.firstFlightDate);
    travelDate.setDate(travelDate.getDate() - 1);
    const yesterday = travelDate.toLocaleDateString('en-CA');

    const getDateMinusYears = (date: any, years: any) => {
      const newDate = new Date(date);
      newDate.setFullYear(date.getFullYear() - years);
      return newDate.toLocaleDateString('en-CA');
    };

    const addDays = (date: any, days: any) => {
      const newDate = new Date(date);
      newDate.setDate(date.getDate() + days);
      return newDate.toLocaleDateString('en-CA');
    };

    switch (this.type) {
      case 'adult':
        return {
          min: addDays(
            new Date(getDateMinusYears(this.firstFlightDate, 100)),
            2
          ),
          max: getDateMinusYears(this.firstFlightDate, 12),
        };

      case 'child':
        return {
          min: addDays(
            new Date(getDateMinusYears(this.firstFlightDate, 12)),
            2
          ),
          max: getDateMinusYears(this.firstFlightDate, 2),
        };

      case 'infant':
        return {
          min: addDays(new Date(getDateMinusYears(this.firstFlightDate, 2)), 2),
          max: yesterday,
        };

      default:
        return {
          min: getDateMinusYears(this.firstFlightDate, 100),
          max: yesterday,
        };
    }
  }

  async presentCountrySelectionModal() {
    if (this.partnerPid === 'ent') {
      return;
    }
    const modal = await this.modalController.create({
      component: SelectCountryModalComponent,
      backdropDismiss: false,
    });
    modal.onDidDismiss().then(async (data) => {
      if (data?.data) {
        const selectedNationality = {
          CountryCode: data.data.iso2,
          CountryName: data.data.nationality,
        };
        this.passengerDetail.Nationality = selectedNationality;
        this.selectedNationalityData = data.data;
        this.passengerForm
          .get('Nationality')
          ?.setValue(selectedNationality.CountryName);
      }
    });
    return await modal.present();
  }

  async ionViewDidEnter() {}

  handlePassengerDetails() {
    // Mark all fields as touched to show validation errors
    Object.keys(this.passengerForm.controls).forEach((key) => {
      this.passengerForm.get(key)?.markAsTouched();
    });

    // If form is invalid, don't proceed
    if (this.passengerForm.invalid) {
      return;
    }

    // Track analytics event for adding passenger
    this.analyticsService.trackUnified('passenger_form_saved', {
      flow: 'shared',
      outcome: 'success',
      legacy_event_name: 'Add Passenger form saved',
      button: 'add_passenger',
      component: 'passenger-detail-form',
      passenger_type: this.type,
    });

    for (const key in this.passengerForm.value) {
      if (key !== 'Nationality') {
        this.passengerDetail[key] = this.passengerForm.value[key];
      }
    }

    //check for duplicate passenger
    if (this.passengerList.length > 0) {
      this.dupPax = this.passengerList.some((el, index) => {
        if (this.indexofEditedPassenger === index) {
          return false;
        }
        return (
          el.FirstName?.toLowerCase() ===
            this.passengerDetail.FirstName?.toLowerCase() &&
          el.LastName?.toLowerCase() ===
            this.passengerDetail.LastName?.toLowerCase() &&
          el.DateOfBirth === this.passengerDetail.DateOfBirth
        );
      });
    }

    if (!this.dupPax) {
      this.passengerDetail.type = this.type;
      this.passengerDetail.saveGuest = this.saveGuest;
      if (this.saveGuest) {
        this.guestListCache = null;
      }
      this.modalController.dismiss(this.passengerDetail);
    }
  }

  closeModal() {
    this.analyticsService.trackUnified('passenger_form_closed', {
      flow: 'shared',
      outcome: 'success',
      legacy_event_name: 'Passenger Detail Form Modal Closed',
      component: 'passenger-detail-form',
    });
    this.modalController.dismiss();
  }

  navigateForward() {
    this.modalController.dismiss();
  }

  async selectfromGuestList() {
    if (this.isGuestListModalOpen) {
      return;
    }
    this.isGuestListModalOpen = true;

    if (this.guestListCache) {
      this.userGuestList = this.guestListCache;
      await this.openGuestListModal();
      return;
    }

    const loaderInstance = await this.uiService.presentGlobalLoading();

    this.accountService
      .getUserGuestListV2()
      .pipe()
      .subscribe(
        async (response: any) => {
          loaderInstance.dismiss();
          this.userGuestList = response.userGuests;
          this.guestListCache = this.userGuestList;
          await this.openGuestListModal();
        },
        (error) => {
          loaderInstance.dismiss();
          this.isGuestListModalOpen = false;
        }
      );
  }

  private async openGuestListModal() {
    const modal = await this.modalController.create({
      component: UserGuestListV2Component,
      componentProps: {
        userGuestList: this.userGuestList,
      },
    });
    await modal.present();

    const { data } = await modal.onWillDismiss();
    this.isGuestListModalOpen = false;
    if (data) {
      // Title: prefer 'title' field, fall back to 'abbreviation'
      const allowedTitles = this.type === 'adult' ? ['Mr', 'Mrs', 'Ms', 'Dr'] : ['Mr', 'Mrs', 'Ms', 'Miss', 'Mstr'];

      const rawTitle = data['title'] || data['abbreviation'] || '';
      const resolvedTitle = allowedTitles.find(
        (t) => t.toLowerCase() === String(rawTitle).toLowerCase()
      );
      if (resolvedTitle) {
        this.passengerForm.get('Title')?.patchValue(resolvedTitle);
      } else {
        this.passengerForm.get('Title')?.patchValue(null);
      }

      if (data['firstName']) {
        this.passengerForm.get('FirstName')?.patchValue(data['firstName']);
      }
      if (data['lastName']) {
        this.passengerForm.get('LastName')?.patchValue(data['lastName']);
      }

      // Normalize gender: API returns "MALE"/"FEMALE", form expects "Male"/"Female"
      if (data['gender']) {
        const gender = String(data['gender']).toLowerCase();
        const normalizedGender =
          gender === 'male' ? 'Male' : gender === 'female' ? 'Female' : 'Other';
        this.passengerForm.get('Gender')?.patchValue(normalizedGender);
      }

      if (data['dob']) {
        this.passengerForm
          .get('DateOfBirth')
          ?.patchValue(new Date(data['dob']).toISOString().substring(0, 10));
      }

      const nationalityCode = data['nationality'] || '';
      const nationalityName =
        data['nationalityCountryName'] || data['nationality'] || '';
      if (nationalityCode || nationalityName) {
        // Form field shows the country name for display
        this.passengerForm.get('Nationality')?.patchValue(nationalityName);
        // passengerDetail stores the structured object
        this.passengerDetail.Nationality = {
          CountryCode: nationalityCode,
          CountryName: nationalityName,
        };
        // Update selectedNationalityData for subsequent modals (e.g. city picker)
        this.selectedNationalityData = {
          iso2: nationalityCode,
          nationality: nationalityName,
          name: nationalityName,
        };
      } else {
        this.passengerForm.get('Nationality')?.patchValue(null);
        this.passengerDetail.Nationality = null;
        this.selectedNationalityData = null;
      }

      this.analyticsService.trackUnified('guest_selected_from_list', {
        flow: 'shared',
        outcome: 'success',
        legacy_event_name: 'guest_selected_from_list',
        action: 'Select',
        list_type: 'GuestList',
      });
    }
  }

  selectGender(gender: string) {
    this.passengerForm.get('Gender')?.markAsTouched();
    this.passengerForm.get('Gender')?.setValue(gender);
  }
}
